import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Phone, Mail, MapPin } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";

export default function Footer() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      setDone(true);
      setEmail("");
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer className="bg-white border-t border-[#E5E7EB]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div className="lg:col-span-2">
          <div className="mb-4">
            <img
              src="/bbm-logo.png"
              alt="BBM — Best Business Management"
              className="h-14 w-auto object-contain"
            />
          </div>
          <p className="text-[#4B5563] text-sm max-w-md mb-6">{t.footer.tagline}</p>

          <form onSubmit={subscribe} className="flex gap-2 max-w-md" data-testid="footer-newsletter-form">
            <input
              type="email"
              required
              placeholder={t.footer.newsletter_placeholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-3 py-2.5 text-sm border border-[#E5E7EB] rounded-md focus:outline-none focus:border-[#0047FF]"
              data-testid="newsletter-email"
            />
            <button
              type="submit"
              disabled={loading || done}
              data-testid="newsletter-submit"
              className="bbm-btn-primary !py-2.5 !px-4 text-sm"
            >
              {done ? <Check className="w-4 h-4" /> : t.footer.newsletter_cta}
            </button>
          </form>
          {done && (
            <p className="mt-2 text-xs font-mono uppercase tracking-widest text-[#0047FF]" data-testid="newsletter-success">
              {t.footer.newsletter_success}
            </p>
          )}
        </div>

        <div>
          <h4 className="font-mono text-[11px] uppercase tracking-widest text-[#0B132B] mb-4">
            {t.footer.links_services}
          </h4>
          <ul className="space-y-2">
            {t.services.items.slice(0, 5).map((s) => (
              <li key={s.title}>
                <button
                  className="text-sm text-[#4B5563] hover:text-[#0B132B] text-start"
                  onClick={() => scrollTo("services")}
                >
                  {s.title}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-mono text-[11px] uppercase tracking-widest text-[#0B132B] mb-4">
            {t.footer.offices}
          </h4>
          <ul className="space-y-5 text-sm">
            <li data-testid="footer-us-office">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0047FF]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#0047FF]">{t.footer.us}</span>
              </div>
              <div className="space-y-1.5 ps-3.5">
                <div className="flex items-start gap-2 text-[#4B5563]">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>487 College Boulevard, Wenonah, NJ 08090</span>
                </div>
                <a href="tel:+16107140171" className="flex items-center gap-2 text-[#0B132B] hover:text-[#0047FF] transition-colors">
                  <Phone className="w-3.5 h-3.5 shrink-0" />
                  +1 610 714 0171
                </a>
              </div>
            </li>
            <li data-testid="footer-dz-office">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFB800]">{t.footer.dz}</span>
              </div>
              <div className="space-y-1.5 ps-3.5">
                <div className="flex items-start gap-2 text-[#4B5563]">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>Villa 367, Sellier Hydra, Algiers</span>
                </div>
                <a href="tel:+213668418716" className="flex items-center gap-2 text-[#0B132B] hover:text-[#0047FF] transition-colors">
                  <Phone className="w-3.5 h-3.5 shrink-0" />
                  +213 668 41 87 16
                </a>
              </div>
            </li>
            <li>
              <a
                href="mailto:Info@bus-management.com"
                className="flex items-center gap-2 text-[#0B132B] hover:text-[#0047FF] transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-3.5 h-3.5 shrink-0" />
                Info@bus-management.com
              </a>
            </li>
          </ul>

          <div className="mt-6 flex items-center gap-3">
            <Link
              to="/admin"
              data-testid="footer-admin-link"
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-[#4B5563] hover:text-[#0B132B]"
            >
              Admin <ArrowUpRight className="w-3 h-3 bbm-flip" />
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563]">
            © {year} BBM. {t.footer.rights}
          </p>
          <p className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563]">
            US · CN · DZ
          </p>
        </div>
      </div>
    </footer>
  );
}
